/**
 * How a permission READS on screen: the German label, and the literal it stands
 * for (IMM-620.1).
 *
 * Both are shown, everywhere a permission is rendered. The literal is not
 * decoration and not a fallback: it is the identifier the public API docs
 * specify, so the person reading a key's permissions — or ticking them on the
 * issue form — is reading the same token they will write against the API.
 * Prose alone forces them to translate „Leads lesen" back into
 * `workspace:lead:lead-read` by guesswork.
 *
 * The FORMAT lives here, next to the catalogue it annotates, for the same
 * reason the labels do: a second copy is the defect one refactor later. The
 * apps supply their resolved label and render the parts; they do not each
 * decide what the pairing looks like.
 *
 * These are pure string helpers — no `intl` — so the i18n package keeps its
 * dependency-light shape and both render paths can use them.
 */

/**
 * The literal to render ALONGSIDE a label, or `null` when there is nothing to
 * add.
 *
 * `null` when the label lookup degraded to the literal itself: the raw
 * identifier is then already the whole cell, and „`workspace:x:y`
 * `workspace:x:y`" reads as a rendering bug rather than an annotation. Callers
 * treat `null` as "label only".
 */
export function permissionLiteral(label: string, permission: string): string | null {
  return label === permission ? null : permission;
}

/**
 * The plain-text pairing — `Leads lesen (workspace:lead:lead-read)` — for the
 * contexts that can only hold a string: the API-key list cell, `title`
 * tooltips, and anything the backend renders.
 *
 * Markup contexts (checkbox rows, detail fields) render the two parts
 * separately instead, so the literal can carry the monospace styling that makes
 * it legible as an identifier. Both go through {@link permissionLiteral}, so a
 * degraded label prints once in either shape.
 */
export function formatPermission(label: string, permission: string): string {
  const literal = permissionLiteral(label, permission);

  return literal === null ? label : `${label} (${literal})`;
}
